'use client'

import { Check, Copy } from 'lucide-react'

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, toast } from '@ui/web/components'
import { buildUrl } from '@helper/utils/format'
import { useHandleCopy } from '@helper/web/hooks'

import { useAuthClient } from '../../../context'

interface CopyPinMenuProps {
  studentId: string
  studentName: string
  pin: string | null
  /**
   * `menu` offers "Salin PIN" and the ready-made WhatsApp message a teacher sends home. `copy` is
   * one click that copies the PIN — the message is a school-to-parent artefact, so a parent looking
   * at their own child has nothing to send.
   */
  variant?: 'menu' | 'copy'
}

/**
 * Builds the message a teacher sends home. The login link is the organization's child-login URL —
 * the same one the QR poster and `SharePinDialog` hand out — because a student PIN alone is not
 * enough to sign in.
 */
function buildWhatsAppMessage({ name, pin, loginUrl }: { name: string; pin: string; loginUrl: string }) {
  return [
    'Halo Bapak/Ibu 👋',
    '',
    `Mohon ajak ${name} login di sini untuk kemudahan belajar! 👉 ${loginUrl}`,
    '',
    'Lalu masukkan PIN Murid di bawah ini:',
    pin,
    '',
    'Jika ada pertanyaan, jangan ragu untuk menghubungi saya ya.',
    '',
    'Terima kasih 🙏',
  ].join('\n')
}

export function CopyPinMenu({ studentId, studentName, pin, variant = 'menu' }: CopyPinMenuProps) {
  const authClient = useAuthClient()
  const { data: activeOrg } = authClient.useActiveOrganization()
  const { isCopied, handleCopy } = useHandleCopy()

  // Nothing to copy and nothing to compose — a student with no PIN cannot log in at all, so the
  // menu would only offer two ways to paste an empty string.
  if (!pin) return null

  const copied = isCopied(studentId)

  const copyPin = () => {
    handleCopy(pin, studentId)
    toast.success(`Berhasil menyalin PIN ${studentName}`)
  }

  if (variant === 'copy') {
    return (
      <button
        type='button'
        aria-label={`Salin PIN ${studentName}`}
        className='srd:shrink-0 srd:text-blue-100'
        onClick={copyPin}
      >
        {copied ? <Check className='srd:size-4' /> : <Copy className='srd:size-4' />}
      </button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={(props) => (
          <button
            type='button'
            aria-label={`Salin PIN ${studentName}`}
            className='srd:shrink-0 srd:text-blue-100'
            {...props}
          >
            {copied ? <Check className='srd:size-4' /> : <Copy className='srd:size-4' />}
          </button>
        )}
      />

      <DropdownMenuContent align='start' className='srd:w-fit srd:max-w-[150px]'>
        <DropdownMenuItem onClick={copyPin}>Salin PIN</DropdownMenuItem>

        <DropdownMenuItem
          disabled={!activeOrg}
          onClick={() => {
            if (!activeOrg) return

            handleCopy(
              buildWhatsAppMessage({
                name: studentName,
                pin,
                loginUrl: buildUrl('account', `/anak/${activeOrg.id}`).href,
              }),
              studentId,
            )
            toast.success('Pesan salinan ajakan login berhasil disalin')
          }}
        >
          Salin ajakan login
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
